import React, { createRef, Fragment, PureComponent, ReactElement } from 'react';

import uuid from 'uuid/v4';

import { Card, CardProps, CollapseArrow, ContextMenu, Field, Form, Option } from '@philips-emr/tws-components';
import { Placement } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType, ContainerProvider, Value } from '@philips-emr/tws-core';
import { arrayOf, bool, element, eventListener, expression, number, oneOf, oneOfType, string, tuple } from '@philips-emr/tws-core/lib/component/prop-types';
import { RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { classNames, enumValues, getLostProps, getSlot, isFunction, isValidWidth, propagateData } from '@philips-emr/tws-utils';

import { shouldRenderMobile } from '../../utils/utils';

import './hpms-custom-card.scss';
import HpmsCustomCardApi, { ACTION_TYPE, DYNAMIC_CONTENT_TYPE } from './hpms-custom-card-api';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent } from './menu-card-type';

const acceptedTypesCollapsed = enumValues(ComponentType) as ComponentType[];

const headerAcceptedTypes = [
  ComponentType.BEHAVIOR,
  ComponentType.DATA_DISPLAY,
  ComponentType.LAYOUT,
];

enum ActionAlign {
  INITIAL = 'initial',
  CENTER = 'center',
  START = 'flex-start',
  END = 'flex-end',
  STRETCH = 'stretch'
}

enum ActionJustify {
  INITIAL = 'initial',
  CENTER = 'center',
  START = 'flex-start',
  END = 'flex-end',
  STRETCH = 'stretch',
  SPACEAROUND = 'space-around',
  SPACEBETWEEN = 'space-between',
  SPACEEVENLY = 'space-evenly'
}

const SLOT_FORM_CONTENT = 'form_content';
const SLOT_COLLAPSE = 'collapse';
const SLOT_HEADER_COLLAPSE = 'slot_header_collapse';

export interface HpmsCustomCardProps extends CardProps {
  onCreateOption: (event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>) => void;
  onCreateMenu: (event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>) => void;
  onDynamicContentRender: (event: RenderEvent) => void;
  onRenderAction: (event: RenderEvent) => void;
  options?: Record<string, string>;
  onOptionClick: (event: HpmsCustomCardEvent<Record<string, Value>>) => void;
  hasDynamicContent: boolean;
  showMenu: boolean;
  dynamicContentWidth?: string;
  fieldsAreaWidth?: string;
  justifyContent?: ActionJustify;
  alignItems?: ActionAlign;
  data: Record<string, Value>[];
  hideDivActionIfActionBlank?: boolean;
  hasCollapse: boolean;
  initialCollapsed: boolean;
  widthHeaderCollapse: number;
  paddingHeaderCollapse: number;
  collapseInsideCard: boolean;
  showDefaultButtonCollapse: boolean;
  showInsideFormButtonCollapse: boolean;
  hideActionCollapseIfEmptyResult: boolean;
  visible: boolean;
  background: string;
}

const customCardPropTypes = {
  children: oneOfType([element, arrayOf(element)]).notEnumerable(),
  onCreateOption: eventListener,
  onCreateMenu: eventListener,
  onDynamicContentRender: eventListener,
  onRenderAction: eventListener,
  options: tuple([
    string.placeholder('option key'),
    expression.placeholder('title'),
  ]).group('custom-card'),
  onOptionClick: eventListener,
  hasDynamicContent: bool.group('custom-card'),
  showMenu: bool.group('custom-card'),
  dynamicContentWidth: string.group('custom-card'),
  fieldsAreaWidth: string.group('custom-card'),
  hideDivActionIfActionBlank: bool.group('custom-card'),
  justifyContent: oneOf(enumValues(ActionJustify)).group('actions-alignment'),
  alignItems: oneOf(enumValues(ActionAlign)).group('actions-alignment'),
  hasCollapse: bool.group('collapse'),
  initialCollapsed: bool.group('collapse'),
  widthHeaderCollapse: number.placeholder('wdth header').group('collapse'),
  paddingHeaderCollapse: string.group('collapse'),
  collapseInsideCard: bool.group('collapse'),
  showDefaultButtonCollapse: bool.group('collapse'),
  showInsideFormButtonCollapse: bool.group('collapse'),
  hideActionCollapseIfEmptyResult: bool.group('collapse'),
  background: string.group('custom-card'),
};

interface HpmsCustomCardState {
  collapsed: boolean,
  showActionCollapse: boolean,
  isCollapsed: boolean,
  background: string,
}

@AwesomeComponent({ api: HpmsCustomCardApi, type: ComponentType.DATA_DISPLAY })
class HpmsCustomCard extends PureComponent<HpmsCustomCardProps, HpmsCustomCardState> {

  refContextMenu = createRef<ContextMenu>();

  public state = {
    collapsed: false,
    isCollapsed: !this.props.initialCollapsed,
    showActionCollapse: true,
    visible: this.props.visible,
    background: this.props.background,
  };

  static propTypes = {
    ...Card.propTypes,
    ...customCardPropTypes,
  };

  static defaultProps = {
    ...Card.defaultProps,
    hasDynamicContent: false,
    showMenu: true,
    fieldsAreaWidth: '70%',
    justifyContent: ActionJustify.CENTER,
    alignItems: ActionAlign.START,
    hideDivActionIfActionBlank: false,
    hasCollapsed: true,
    initialCollapsed: false,
    showDefaultButtonCollapse: true,
    showInsideFormButtonCollapse: false,
    widthHeaderCollapse: 3,
    hideActionCollapseIfEmptyResult: false,
    visible: true,
  };

  getCardData(): Record<string, Value> {
    return getLostProps(HpmsCustomCard.propTypes, this.props);
  }

  onOptionClick = (key: string): void => {
    const { onOptionClick } = this.props;

    if (isFunction(onOptionClick)) {
      onOptionClick(new HpmsCustomCardEvent<Record<string, Value>>({
        data: this.getCardData(),
        key,
      }));

      this.refContextMenu.current?.setState({ open: false });
    }
  };

  getOptions(): ReactElement[] | ReactElement {
    const { options, onCreateOption } = this.props;

    if (!options) {
      return <Fragment />;
    }

    const optionProps = {
      enabled: true,
      preventDefault: true,
      stopPropagation: true,
      visible: true,
    };

    return Object.entries(options).map(([key, value]) => {
      let shouldRender = true;

      if (isFunction(onCreateOption)) {
        const event = new HpmsCustomCardMenuRenderEvent<Record<string, Value>>({
          key,
          data: this.getCardData(),
          shouldRender: (render: boolean) => (shouldRender = render),
        });
        onCreateOption(event);
      }

      return (
        shouldRender
          ? <Option
            key={key}
            {...optionProps}
            onClick={() => this.onOptionClick(key)}
            value={value}
          />
          : <Fragment key={key} />
      );
    });
  }

  renderAction(): ReactElement | null {
    const {
      alignItems = ActionAlign.START,
      showMenu, justifyContent = ActionJustify.CENTER,
      onRenderAction,
      onCreateMenu,
      hideDivActionIfActionBlank,
    } = this.props;

    let action = null;

    if (isFunction(onRenderAction)) {
      let renderFn = (): ReactElement | null => {
        return null;
      };

      const render = (fn: () => ReactElement): void => {
        renderFn = fn;
      };

      onRenderAction(
        new RenderEvent({
          data: this.getCardData(),
          entry: ACTION_TYPE,
          render,
          type: ACTION_TYPE,
        })
      );

      action = renderFn();
    }

    let shouldRenderMenu = showMenu;

    if (isFunction(onCreateMenu)) {
      const event = new HpmsCustomCardMenuRenderEvent<Record<string, Value>>({
        key: 'MENU',
        data: this.getCardData(),
        shouldRender: (render: boolean) => (shouldRenderMenu = render),
      });

      onCreateMenu(event);
    }

    if (shouldRenderMenu) {
      action = (
        <ContextMenu
          placement={Placement.LEFT}
          uuid={uuid()}
          ref={this.refContextMenu}
        >
          {this.getOptions()}
        </ContextMenu>
      );
    }

    if (!action && hideDivActionIfActionBlank) {
      return null;
    }

    return (
      <div className="c-hpms-custom-card__actions" style={{ alignItems, justifyContent }}>
        {action}
      </div>
    );
  }

  renderForm(): ReactElement {
    const { uuid, fieldsAreaWidth, hasCollapse, showInsideFormButtonCollapse } = this.props;
    const data = getLostProps(HpmsCustomCard.propTypes, this.props);
    let children;

    if (hasCollapse) {
      children = this.getChildren(SLOT_FORM_CONTENT);

      if (showInsideFormButtonCollapse) {
        children = [...(children as []), this.renderFieldBottonCollapse()];
      }
    } else {
      children = this.props.children;
    }

    return (
      <div className="c-hpms-custom-card__form" style={{ width: fieldsAreaWidth }}>
        <Form data={data} locked={true} editing={false} disabled uuid={uuid} slot={hasCollapse ? SLOT_FORM_CONTENT : undefined}>
          {children}
        </Form>
      </div>
    );
  }

  private renderFieldBottonCollapse(): ReactElement {
    const { widthHeaderCollapse } = this.props;
    const { collapsed, showActionCollapse, isCollapsed } = this.state;

    return (
      <Field uuid={uuid()} entry="" label="" width={widthHeaderCollapse}>
        <div onClick={() => this.onClickHeader(isCollapsed)}>
          {showActionCollapse && <CollapseArrow collapsed={collapsed} />}
        </div>
      </Field>
    );

  }

  renderDynamicContent(): ReactElement | null {
    const { onDynamicContentRender, hasDynamicContent, dynamicContentWidth } = this.props;

    if (!hasDynamicContent) {
      return null;
    }

    let renderFn = (): ReactElement | null => {
      return (
        <Fragment />
      );
    };

    if (isFunction(onDynamicContentRender)) {
      const render = (fn: () => ReactElement): void => {
        renderFn = fn;
      };

      onDynamicContentRender(
        new RenderEvent({
          data: this.getCardData(),
          entry: DYNAMIC_CONTENT_TYPE,
          render,
          type: DYNAMIC_CONTENT_TYPE,
        })
      );
    }

    return (
      <div className="c-hpms-custom-card__dynamic-content" style={{ width: dynamicContentWidth }}>
        {renderFn()}
      </div>
    );
  }

  public componentDidMount(): void {
    this.setState({ collapsed: this.props.hasCollapse && this.props.initialCollapsed });
  }

  private getContent(): ReactElement {
    const { showDefaultButtonCollapse, transparent, background } = this.props;
    const className = `c-hpms-custom-card__content-wrapper ${shouldRenderMobile() ? 'is-mobile' : ''}`;
    if (!transparent) {
      return (
        <div className={className} style={{ background }}>
          <div className="c-hpms-custom-card__content" >
            {this.renderForm()}
            {this.renderDynamicContent()}
          </div>
          {this.renderAction()}
          {showDefaultButtonCollapse && this.renderHeader()}
        </div>
      );
    }
    return (
      <div className={className}>
        <div className="c-hpms-custom-card__content" >
          {this.renderForm()}
          {this.renderDynamicContent()}
        </div>
        {this.renderAction()}
        {showDefaultButtonCollapse && this.renderHeader()}
      </div>
    );
  }

  private onClickHeader(open: boolean): void {
    this.setState(({ collapsed }) => ({ collapsed: !collapsed }));
    this.setState({ isCollapsed: !open });
  }

  private getChildren(slot: string): ReactElement<any>[] {
    return getSlot(this.props.children, slot);
  }

  private renderHeader(): ReactElement | null {
    const Container = ContainerProvider.provide(this);

    const { hasCollapse, widthHeaderCollapse, alignItems, justifyContent, uuid } = this.props;
    const { showActionCollapse } = this.state;

    if (!hasCollapse || !Container) {
      return null;
    }

    const { collapsed, isCollapsed } = this.state;
    const clazz = this.getClassNameHeader(widthHeaderCollapse);
    const children = this.getChildren(SLOT_HEADER_COLLAPSE);

    const props = { ...(!shouldRenderMobile() && { onClick: () => this.onClickHeader(isCollapsed) }) };

    return (
      <div
        className={this.getClassNameHeader(widthHeaderCollapse)}
        {...props}>

        <Container
          acceptedTypes={headerAcceptedTypes}
          slot={SLOT_HEADER_COLLAPSE}
          uuid={uuid}
        >
          <div className={clazz} style={{ alignItems, justifyContent }}>
            {children}
          </div>
        </Container>

        {showActionCollapse && <CollapseArrow collapsed={collapsed} />}
      </div>
    );
  }

  private getClassNameHeader(width: number): string {
    return classNames({
      [`u-col-${width}`]: isValidWidth(width),
      ['c-hpms-custom-card__header-accordion']: true,
    });
  }

  public render(): ReactElement | null {
    const { collapseInsideCard } = this.props;
    const { visible } = this.state;
    if(!visible){
      return null;
    }
    if (collapseInsideCard) {
      return this.getCardCollapseInsideCard();
    }

    return this.getCardDefault();
  }

  private getCardDefault(): ReactElement {
    const cardProps = { ...getLostProps(customCardPropTypes, this.props) };

    const { hasCollapse, showDefaultButtonCollapse } = this.props;
    const { collapsed, isCollapsed } = this.state;

    let component: ReactElement;

    if (shouldRenderMobile()) {
      component = (
        <>
          <div onClick={() => this.onClickHeader(isCollapsed)} className="c-container">
            {this.getContent()}
            {showDefaultButtonCollapse && this.renderHeader()}
          </div>
        </>
      );
    } else {
      component = (
        <>
          {this.getContent()}
          {showDefaultButtonCollapse && this.renderHeader()}
        </>
      );
    }

    return (
      <>
        <Card {...cardProps}>
          {component}
        </Card>

        {hasCollapse && !collapsed && this.getContentCollapsed()}
      </>
    );
  }

  private getCardCollapseInsideCard(): ReactElement {
    const cardProps = { ...getLostProps(customCardPropTypes, this.props) };
    const { hasCollapse, transparent, background } = this.props;
    const { collapsed, isCollapsed } = this.state;
    const props = { ...(shouldRenderMobile() && { onClick: () => this.onClickHeader(isCollapsed) }) };
    if (!transparent) {
      return (
        <Card {...cardProps}>
          <>
            <div className="c-hpms-custom-card__accordion-inside-card-content">
              <div className="c-card__content c-container" {...props} style={{ background }}>
                {this.getContent()}
              </div>
              {hasCollapse && !collapsed && this.getContentCollapsed()}
            </div>
          </>
        </Card>
      );
    }

    return (
      <Card {...cardProps}>
        <>
          <div className="c-hpms-custom-card__accordion-inside-card-content">
            { !transparent ?
              <div className="c-card__content c-container" {...props}>
                {this.getContent()}
              </div>
              :
              <div className="c-card__content c-container" {...props}>
                {this.getContent()}
              </div>
            }
            {hasCollapse && !collapsed && this.getContentCollapsed()}
          </div>
        </>
      </Card>
    );
  }

  private getContentCollapsed(): ReactElement | null {
    const Container = ContainerProvider.provide(this);
    const { uuid, fieldsAreaWidth, hideActionCollapseIfEmptyResult } = this.props;
    let onAfterFetchtHideAction = null;

    if (!Container) {
      return null;
    }

    if (hideActionCollapseIfEmptyResult) {
      onAfterFetchtHideAction = (): void => this.setState({ ...this.state, showActionCollapse: false });
    }

    const children = this.getChildren(SLOT_COLLAPSE);
    const data = { ...getLostProps(HpmsCustomCard.propTypes, this.props), onAfterFetchtHideAction, ...this.state };

    return (
      <Container acceptedTypes={acceptedTypesCollapsed} uuid={uuid} slot={SLOT_COLLAPSE} className="u-grid-col-12">
        <div style={{ width: fieldsAreaWidth }}>
          {propagateData(children, data)}
        </div>
      </Container>
    );
  }

}

export default HpmsCustomCard;
